import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import {
  CatalogPlanDto, ChangePlanRequestDto, ClinicSubscriptionSummaryDto,
  ConfirmPaymentMethodUpdateRequestDto, PaymentMethodDto, PlanChangePreviewDto,
  StartPaymentMethodUpdateResultDto,
} from '../../dtos/organization-subscription.dto';

@Injectable()
export class OrganizationSubscriptionRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public get(organizationId: number): Observable<ClinicSubscriptionSummaryDto> {
    return this.http.get<ClinicSubscriptionSummaryDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.subscription(organizationId)}`,
    );
  }

  public setAutoCharge(organizationId: number, enabled: boolean): Observable<ClinicSubscriptionSummaryDto> {
    return this.http.put<ClinicSubscriptionSummaryDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.subscriptionAutoCharge(organizationId)}`,
      { Enabled: enabled },
    );
  }

  public getPlans(): Observable<readonly CatalogPlanDto[]> {
    return this.http.get<readonly CatalogPlanDto[]>(`${this.apiUrl}${API_ENDPOINTS.onboarding.plans}`);
  }

  public getPlanChangePreview(
    organizationId: number,
    planId: string,
    billingPeriod: string,
  ): Observable<PlanChangePreviewDto> {
    return this.http.get<PlanChangePreviewDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.subscriptionPlanChangePreview(organizationId)}`,
      { params: { planId, billingPeriod } },
    );
  }

  public changePlan(
    organizationId: number,
    planId: string,
    billingPeriod: string,
  ): Observable<ClinicSubscriptionSummaryDto> {
    const body: ChangePlanRequestDto = { PlanId: planId, BillingPeriod: billingPeriod };
    return this.http.put<ClinicSubscriptionSummaryDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.subscriptionPlan(organizationId)}`,
      body,
    );
  }

  public getPaymentMethod(organizationId: number): Observable<PaymentMethodDto | null> {
    return this.http.get<PaymentMethodDto | null>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.platformBillingPaymentMethod(organizationId)}`,
    );
  }

  public startPaymentMethodUpdate(organizationId: number): Observable<StartPaymentMethodUpdateResultDto> {
    return this.http.post<StartPaymentMethodUpdateResultDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.platformBillingPaymentMethodStart(organizationId)}`,
      null,
    );
  }

  public confirmPaymentMethodUpdate(
    organizationId: number,
    setupIntentId: string,
  ): Observable<PaymentMethodDto> {
    const body: ConfirmPaymentMethodUpdateRequestDto = { SetupIntentId: setupIntentId };
    return this.http.post<PaymentMethodDto>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.platformBillingPaymentMethodConfirm(organizationId)}`,
      body,
    );
  }

  public removePaymentMethod(organizationId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}${API_ENDPOINTS.organizations.platformBillingPaymentMethod(organizationId)}`,
    );
  }
}
